import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

const QUALITY = [
  { v: "poor", l: "Poor" },
  { v: "fair", l: "Fair" },
  { v: "good", l: "Good" },
  { v: "excellent", l: "Excellent" },
];
const CROWD = [
  { v: "almost_empty", l: "Almost empty" },
  { v: "quiet", l: "Quiet" },
  { v: "moderate", l: "Moderate" },
  { v: "busy", l: "Busy" },
  { v: "very_busy", l: "Very busy" },
];
const BEG = [
  { v: "suitable", l: "Suitable" },
  { v: "marginal", l: "Marginal" },
  { v: "unsuitable", l: "Unsuitable" },
  { v: "not_sure", l: "Not sure" },
];
const WAVE = [
  { v: "flat", l: "Flat" },
  { v: "ankle_knee", l: "Ankle–knee" },
  { v: "knee_waist", l: "Knee–waist" },
  { v: "waist_chest", l: "Waist–chest" },
  { v: "chest_head", l: "Chest–head" },
  { v: "overhead", l: "Overhead" },
  { v: "not_sure", l: "Not sure" },
];

function Row({ label, options, value, onChange, testKey }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            data-testid={`${testKey}-${o.v}`}
            className={`px-3 py-1.5 rounded-full text-xs border ${
              value === o.v
                ? "bg-teal-800 text-white border-teal-800"
                : "bg-white border-border hover:border-teal-800/50"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReportModal({ surfAreaId, areaName, onClose, onSubmitted }) {
  const [quality, setQuality] = useState("good");
  const [crowd, setCrowd] = useState("moderate");
  const [beg, setBeg] = useState("suitable");
  const [wave, setWave] = useState("waist_chest");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reports", {
        surf_area_id: surfAreaId,
        surf_quality: quality,
        crowd_level: crowd,
        beginner_suitable: beg,
        wave_category: wave,
        comment: comment.trim() || null,
      });
      toast.success("Report submitted");
      onSubmitted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-4" data-testid="report-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="overline">Report conditions</div>
            <div className="font-display text-lg text-teal-900">{areaName}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="report-close"
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-sand-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <Row label="Surf quality" options={QUALITY} value={quality} onChange={setQuality} testKey="rep-quality" />
          <Row label="Crowd" options={CROWD} value={crowd} onChange={setCrowd} testKey="rep-crowd" />
          <Row label="Beginner suitable?" options={BEG} value={beg} onChange={setBeg} testKey="rep-beg" />
          <Row label="Wave size" options={WAVE} value={wave} onChange={setWave} testKey="rep-wave" />
          <div>
            <div className="overline">Optional note (500 chars)</div>
            <textarea
              data-testid="rep-comment"
              maxLength={500}
              className="mt-2 w-full h-24 rounded-xl border border-border bg-white p-3 text-sm outline-none focus:border-teal-800"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="e.g. Sets are coming through in the corner, watch the rip on the north end."
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-border bg-sand-100">
          <button
            type="button"
            onClick={onClose}
            data-testid="rep-cancel"
            className="h-10 px-4 rounded-full text-sm text-muted-foreground hover:bg-sand-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            data-testid="rep-submit"
            disabled={busy}
            className="h-10 px-5 rounded-full bg-terracotta-500 text-white text-sm font-medium hover:bg-terracotta-600 disabled:opacity-60"
          >
            {busy ? "Submitting…" : "Submit report"}
          </button>
        </div>
      </div>
    </div>
  );
}
